import { useQuery } from '@tanstack/react-query';
import { LaborLawFinesConsult, SocialSecurityArrear } from 'lib/types/models';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import {
  BusinessInvoicesSummary,
  CessionsSummary,
  Signer,
  SignersResponse,
} from './types';

export const getSIIBusinessDetails = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/sii/details`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessPartners = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/sii/partners`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessAddresses = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/sii/addresses`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessCreditNotes = async (
  dispatch: Dispatch,
  id: string,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/creditNotes?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessInvoices = async (
  dispatch: Dispatch,
  id: string,
  type: string,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/invoices?type=${type}&${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getIvaFolderLink = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/iva/folder`,
    dispatch,
  });
  if (response) return response.data;
};

export const getIvaCompact = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/iva/compact`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAssignatedExecutives = async (
  dispatch: Dispatch,
  id: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/executives`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessFiles = async (
  dispatch: Dispatch,
  businessId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/${businessId}/open/files`,
    dispatch,
    data,
    responseType: 'json',
  });
  if (response) return response.data;
};

export const uploadBusinessFile = async (
  dispatch: Dispatch,
  businessId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/${businessId}/open/files/upload`,
    dispatch,
    data,
    responseType: 'json',
  });
  if (response) return response.data;
};

export const deleteBusinessFile = async (
  dispatch: Dispatch,
  businessId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/${businessId}/open/files/delete`,
    dispatch,
    data,
    responseType: 'json',
  });
  if (response) return response.data;
};

export const downloadBusinessFile = async (
  dispatch: Dispatch,
  businessId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/${businessId}/open/files/download`,
    dispatch,
    data,
    responseType: 'json',
  });
  if (response) return response.data;
};

export const createBusinessFolder = async (
  dispatch: Dispatch,
  businessId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/${businessId}/open/files/createFolder`,
    dispatch,
    data,
    responseType: 'json',
  });
  if (response) return response.data;
};

export const getRelatedBusinesses = async (dispatch: Dispatch, id: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/open/relatedBusinesses`,
    dispatch,
  });
  if (response) return response.data;
};

export const getRelatedBusiness = async (
  dispatch: Dispatch,
  id: string,
  relatedBusinessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/open/relatedBusinesses/${relatedBusinessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessOpen = async (
  dispatch: Dispatch,
  identifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${identifier}/open/profile`,
    dispatch,
  });
  if (response) return response.data;
};

export const createBusinessRelation = async (
  dispatch: Dispatch,
  identifier: string,
  data: {
    businessToIdentifier: string;
    businessToName: string;
  },
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/${identifier}/open/relatedBusinesses`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const deleteRelatedBusiness = async (
  dispatch: Dispatch,
  businessFrom: string,
  businessToIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/business/${businessFrom}/open/relatedBusinesses`,
    dispatch,
    data: {
      businessToIdentifier,
    },
  });
  if (response) return response.data;
};

export const getRutificator = async (dispatch: Dispatch, ruts: string[]) => {
  const query = ruts.map((rut) => `ruts=${rut}`).join('&');
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/rutificator?${query}`,
    dispatch,
    responseType: 'json',
  });
  if (response) return response.data;
};

export const getStakeholderProfileData = async (
  dispatch: Dispatch,
  businessId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/stakeholderProfile/${businessId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getOperativeProfileData = async (
  dispatch: Dispatch,
  businessId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/operativeProfile/${businessId}`,
    dispatch,
  });
  if (response) return response.data;
};

export const createBusinessSimple = async (dispatch: Dispatch, data: any) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/business/simple`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const getBusinessUnits = async (
  dispatch: Dispatch,
  identifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${identifier}/open/units`,
    dispatch,
  });
  if (response) return response.data;
};

export const searchOrCreateBusiness = async (
  dispatch: Dispatch,
  identifier: string,
  data?: {
    name: string;
  },
) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/business/searchOrCreate/${identifier}`,
    dispatch,
    data: data ?? {},
  });
  if (response) return response.data;
};

export const getSigners = (dispatch: Dispatch, identifier?: string) =>
  useQuery({
    queryKey: ['businessProfile', 'signers', identifier],
    queryFn: async (): Promise<Signer[] | undefined> => {
      const query = identifier ? `?identifier=${identifier}` : '';
      const response = await requestHandler({
        method: 'get',
        url: `internal/business/signers${query}`,
        dispatch,
      });
      if (response) return response.data;
      return [];
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const getAllSigners = async (
  dispatch: Dispatch,
  query: string,
): Promise<SignersResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/signers/all?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSocialSecurityArrear = (
  dispatch: Dispatch,
  identifier: string,
) =>
  useQuery({
    queryKey: ['businessProfile', identifier, 'socialSecurityArrear'],
    queryFn: async (): Promise<SocialSecurityArrear[]> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/business/${identifier}/open/socialSecurityArrear`,
        dispatch,
      });
      if (response) return response.data;
      return [];
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const getLaborLawFines = (dispatch: Dispatch, identifier: string) =>
  useQuery({
    queryKey: ['businessProfile', identifier, 'laborLawFines'],
    queryFn: async (): Promise<LaborLawFinesConsult> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/business/${identifier}/open/laborLawFines`,
        dispatch,
      });
      if (response) return response.data;
      return {
        updatedAt: null,
        data: [],
      };
    },
    staleTime: 1000 * 60 * 60 * 12,
    retry: 1,
  });

export const getBusinessInvoicesSummary = async (
  dispatch: Dispatch,
  id: string,
  type: string,
): Promise<BusinessInvoicesSummary | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/business/${id}/${type}/summary`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCessionsSummary = (
  dispatch: Dispatch,
  id: string | undefined,
) =>
  useQuery({
    queryKey: ['businessProfile', id, 'cessionsSummary'],
    queryFn: async (): Promise<CessionsSummary | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/business/${id}/cessions/summary`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    retry: 1,
  });
